import useOpenApiClient from '@/hooks/useOpenApiClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useRef } from 'react';
import { useLocation } from 'wouter';

function useAddReceipt() {
  const { apiFetch } = useOpenApiClient();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const uploadReceiptMutation = useMutation({
    mutationKey: ['v1', 'receipts'],
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file, file.name);
      return apiFetch?.POST('/receipts', {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: formData as any,
      });
    },
    onMutate: () => {
      setLocation(`/upload-success/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['v1', 'receipts'],
        refetchType: 'active',
      });
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      uploadReceiptMutation.mutate(file);
    }
  };

  const onCancel = () => {
    if (fileInputRef.current && cameraInputRef.current) {
      fileInputRef.current.value = '';
      cameraInputRef.current.value = '';
    }
  };

  const onCameraClick = () => cameraInputRef.current?.click();
  const onUploadClick = () => fileInputRef.current?.click();

  return {
    cameraInputRef,
    fileInputRef,
    handleFileChange,
    onCameraClick,
    onUploadClick,
    onCancel,
    uploadReceiptMutation,
  };
}
type AddReceiptContextType = Omit<
  ReturnType<typeof useAddReceipt>,
  'cameraInputRef' | 'handleFileChange' | 'fileInputRef'
>;
export const AddReceiptContext = createContext<AddReceiptContextType>(
  {} as unknown as AddReceiptContextType,
);

export const AddReceiptProvider = ({ children }: { children: React.ReactNode }) => {
  const { cameraInputRef, handleFileChange, fileInputRef, ...value } = useAddReceipt();
  return (
    <AddReceiptContext.Provider value={value}>
      {children}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="*/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </AddReceiptContext.Provider>
  );
};

export const useAddReceiptContext = () => useContext(AddReceiptContext);
